import { Checkbox } from '../checkbox';
import { FormControl } from '../formControl';
import { Input } from '../input';
import { SwitchGroup } from '../switchGroup';
import { showErrorNotification } from '../notificationToaster';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { graphql } from 'relay-runtime';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import type { ToolUsageType, usageControlsMutationsCreateMutation } from './__generated__/usageControlsMutationsCreateMutation.graphql';
import type { usageControlsMutationsUpdateMutation } from './__generated__/usageControlsMutationsUpdateMutation.graphql';
import { createToolUsageRuleMutation, updateToolUsageRuleMutation } from './usageControlsMutations';
import Stack from '../stack/stack';

const CheckboxContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
`;

const LimitRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    flex-wrap: wrap;
`;

const InputLabel = styled.span`
    font-size: 13px;
    white-space: nowrap;
`;

const SwitchLabelContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const InputInline = styled(Input)`
    width: 52px;
    padding: 5px 6px;
    font-size: 13px;
`;

const PeriodSelect = styled.select`
    padding: 5px 6px;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: 4px;
    font-size: 13px;
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 16px 0;
`;

const usageControlsQueryDef = graphql`
    query usageControlsQuery($toolType: ToolUsageType!) {
        toolUsageRuleByToolType(toolType: $toolType) {
            toolUsageRuleID
            applyToAllTools
            allowLoggedUsers
            allowNonLoggedUsers
            limitEnabled
            limitPeriodType
            maxOperations
            limitPeriodMinutes
        }
    }
`;

interface ToolUsageRuleSettings {
    toolUsageRuleID: number | null;
    allowLoggedUsers: boolean;
    allowNonLoggedUsers: boolean;
    limitEnabled: boolean;
    limitPeriodType: 'SESSION' | 'MINUTES';
    maxOperations: number;
    limitPeriodMinutes: number;
}

const defaultSettings: ToolUsageRuleSettings = {
    toolUsageRuleID: null,
    allowLoggedUsers: true,
    allowNonLoggedUsers: true,
    limitEnabled: false,
    limitPeriodType: 'SESSION',
    maxOperations: 10,
    limitPeriodMinutes: 5
};

interface UsageControlsQueryResponse {
    toolUsageRuleByToolType: {
        toolUsageRuleID: number;
        applyToAllTools: boolean;
        allowLoggedUsers: boolean;
        allowNonLoggedUsers: boolean;
        limitEnabled: boolean;
        limitPeriodType: string | null;
        maxOperations: number | null;
        limitPeriodMinutes: number | null;
    } | null;
}

const buildSettings = (data: UsageControlsQueryResponse): ToolUsageRuleSettings => {
    if (data.toolUsageRuleByToolType) {
        return {
            toolUsageRuleID: data.toolUsageRuleByToolType.toolUsageRuleID,
            allowLoggedUsers: data.toolUsageRuleByToolType.allowLoggedUsers,
            allowNonLoggedUsers: data.toolUsageRuleByToolType.allowNonLoggedUsers,
            limitEnabled: data.toolUsageRuleByToolType.limitEnabled,
            limitPeriodType: (data.toolUsageRuleByToolType.limitPeriodType as 'SESSION' | 'MINUTES') ?? 'SESSION',
            maxOperations: data.toolUsageRuleByToolType.maxOperations ?? 10,
            limitPeriodMinutes: data.toolUsageRuleByToolType.limitPeriodMinutes ?? 5
        };
    }
    return { ...defaultSettings };
};

export interface UsageControlsRef {
    save: () => Promise<boolean>;
    hasChanges: () => boolean;
    /** Resets settings to their default values and persists the reset. */
    resetToDefault: () => Promise<boolean>;
}

export interface UsageControlsProps {
    toolType: ToolUsageType;
    sectionTitle?: string;
    /** Label for the operation type (e.g., "requests", "iterations"). Defaults to "requests" */
    operationLabel?: string;
    /** Called when the validation state changes (at least one user type must be checked) */
    onValidChange?: (isValid: boolean) => void;
    /** Called when the dirty state changes (current settings differ from the saved baseline) */
    onDirtyChange?: (isDirty: boolean) => void;
    /** When true, highlights the checkbox area with red outline */
    highlightCheckboxes?: boolean;
}

const UsageControls = forwardRef<UsageControlsRef, UsageControlsProps>(
    ({ toolType, sectionTitle, operationLabel = T._('requests', 'Admin'), onValidChange, onDirtyChange, highlightCheckboxes }, ref) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const queryData = useLazyLoadQuery<any>(usageControlsQueryDef, { toolType }, { fetchPolicy: 'network-only' }) as UsageControlsQueryResponse;

        const [settings, setSettings] = useState<ToolUsageRuleSettings>(buildSettings(queryData));
        const [initialSettings, setInitialSettings] = useState<ToolUsageRuleSettings>(buildSettings(queryData));

        const [createRule] = useMutation<usageControlsMutationsCreateMutation>(createToolUsageRuleMutation);
        const [updateRule] = useMutation<usageControlsMutationsUpdateMutation>(updateToolUsageRuleMutation);

        useEffect(() => {
            const newSettings = buildSettings(queryData);
            setSettings(newSettings);
            setInitialSettings(newSettings);
        }, [queryData]);

        useEffect(() => {
            onValidChange?.(settings.allowLoggedUsers || settings.allowNonLoggedUsers);
        }, [settings.allowLoggedUsers, settings.allowNonLoggedUsers, onValidChange]);

        const hasChanges = (): boolean => {
            return JSON.stringify(settings) !== JSON.stringify(initialSettings);
        };

        useEffect(() => {
            onDirtyChange?.(JSON.stringify(settings) !== JSON.stringify(initialSettings));
        }, [settings, initialSettings, onDirtyChange]);

        const handleAllowLoggedUsersChange = (checked: boolean) => {
            setSettings({ ...settings, allowLoggedUsers: checked });
        };

        const handleAllowNonLoggedUsersChange = (checked: boolean) => {
            setSettings({ ...settings, allowNonLoggedUsers: checked });
        };

        const handleLimitEnabledChange = (checked: boolean) => {
            const newSettings = { ...settings, limitEnabled: checked };
            if (checked) {
                newSettings.maxOperations = settings.maxOperations || 10;
                newSettings.limitPeriodMinutes = settings.limitPeriodMinutes || 5;
            }
            setSettings(newSettings);
        };

        const handleLimitPeriodTypeChange = (periodType: 'SESSION' | 'MINUTES') => {
            const newSettings = { ...settings, limitPeriodType: periodType };
            if (periodType === 'SESSION') {
                newSettings.maxOperations = settings.maxOperations || 10;
            } else {
                newSettings.maxOperations = settings.maxOperations || 10;
                newSettings.limitPeriodMinutes = settings.limitPeriodMinutes || 5;
            }
            setSettings(newSettings);
        };

        const handleMaxOperationsChange = (value: string) => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue) && numValue >= 1) {
                setSettings({ ...settings, maxOperations: numValue });
            }
        };

        const handleLimitPeriodMinutesChange = (value: string) => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue) && numValue >= 1) {
                setSettings({ ...settings, limitPeriodMinutes: numValue });
            }
        };

        const persistSettings = (settingsToPersist: ToolUsageRuleSettings): Promise<boolean> => {
            return new Promise((resolve) => {
                if (JSON.stringify(settingsToPersist) === JSON.stringify(initialSettings)) {
                    resolve(true);
                    return;
                }

                if (!settingsToPersist.allowLoggedUsers && !settingsToPersist.allowNonLoggedUsers) {
                    showErrorNotification({
                        message: T._('Please select at least one user type: Logged-in users or Guest users.', 'Admin')
                    });
                    resolve(false);
                    return;
                }

                const limitPeriodTypeValue =
                    settingsToPersist.limitEnabled && settingsToPersist.limitPeriodType
                        ? (settingsToPersist.limitPeriodType.toUpperCase() as 'SESSION' | 'MINUTES')
                        : null;

                const input = {
                    allowLoggedUsers: settingsToPersist.allowLoggedUsers,
                    allowNonLoggedUsers: settingsToPersist.allowNonLoggedUsers,
                    limitEnabled: settingsToPersist.limitEnabled,
                    limitPeriodType: limitPeriodTypeValue,
                    maxOperations: settingsToPersist.limitEnabled ? settingsToPersist.maxOperations : null,
                    limitPeriodMinutes:
                        settingsToPersist.limitEnabled && settingsToPersist.limitPeriodType === 'MINUTES' ? settingsToPersist.limitPeriodMinutes : null,
                    toolType
                };

                if (settingsToPersist.toolUsageRuleID) {
                    updateRule({
                        variables: {
                            input: {
                                toolUsageRuleID: settingsToPersist.toolUsageRuleID,
                                ...input
                            }
                        },
                        onError: (error) => {
                            console.error('Error updating rule:', error);
                            showErrorNotification({ message: T._('Failed to save Usage Controls settings', 'Admin') });
                            resolve(false);
                        },
                        onCompleted: (response) => {
                            if (response.updateToolUsageRule) {
                                const newSettings = {
                                    ...settingsToPersist,
                                    toolUsageRuleID: response.updateToolUsageRule.toolUsageRuleID
                                };
                                setSettings(newSettings);
                                setInitialSettings(newSettings);
                            }
                            resolve(true);
                        }
                    });
                } else {
                    createRule({
                        variables: {
                            input
                        },
                        onError: (error) => {
                            console.error('Error creating rule:', error);
                            showErrorNotification({ message: T._('Failed to save Usage Controls settings', 'Admin') });
                            resolve(false);
                        },
                        onCompleted: (response) => {
                            if (response.createToolUsageRule) {
                                const newSettings = {
                                    ...settingsToPersist,
                                    toolUsageRuleID: response.createToolUsageRule.toolUsageRuleID
                                };
                                setSettings(newSettings);
                                setInitialSettings(newSettings);
                            }
                            resolve(true);
                        }
                    });
                }
            });
        };

        const save = (): Promise<boolean> => persistSettings(settings);

        const resetToDefault = (): Promise<boolean> => {
            const resetSettings: ToolUsageRuleSettings = { ...defaultSettings, toolUsageRuleID: settings.toolUsageRuleID };
            setSettings(resetSettings);
            return persistSettings(resetSettings);
        };

        useImperativeHandle(ref, () => ({
            save,
            hasChanges,
            resetToDefault
        }));

        return (
            <div>
                {sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
                <Stack>
                    <FormControl label={T._('Available for:', 'Admin')}>
                        <CheckboxContainer
                            style={
                                highlightCheckboxes && !settings.allowLoggedUsers && !settings.allowNonLoggedUsers
                                    ? { outline: '2px solid #ef4444', borderRadius: 6, padding: 8, outlineOffset: 2 }
                                    : undefined
                            }
                        >
                            <Checkbox
                                label={T._('Logged-in users', 'Admin')}
                                checked={settings.allowLoggedUsers}
                                onChange={(checked) => handleAllowLoggedUsersChange(checked)}
                            />
                            <Checkbox
                                label={T._('Guest users', 'Admin')}
                                checked={settings.allowNonLoggedUsers}
                                onChange={(checked) => handleAllowNonLoggedUsersChange(checked)}
                            />
                        </CheckboxContainer>
                    </FormControl>

                    <FormControl>
                        <SwitchGroup
                            checked={settings.limitEnabled}
                            onChange={(checked) => handleLimitEnabledChange(checked)}
                            label={<SwitchLabelContainer>{T._('Usage limits', 'Admin')}</SwitchLabelContainer>}
                        />
                    </FormControl>
                </Stack>

                {settings.limitEnabled && (
                    <LimitRow>
                        <InputLabel>{T._('Max', 'Admin')}</InputLabel>
                        <InputInline
                            type='number'
                            min={1}
                            value={settings.maxOperations}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleMaxOperationsChange(e.target.value)}
                        />
                        <InputLabel>
                            {operationLabel} {T._('per user', 'Admin')}
                        </InputLabel>
                        <PeriodSelect value={settings.limitPeriodType} onChange={(e) => handleLimitPeriodTypeChange(e.target.value as 'SESSION' | 'MINUTES')}>
                            <option value='SESSION'>{T._('per session', 'Admin')}</option>
                            <option value='MINUTES'>{T._('every', 'Admin')}</option>
                        </PeriodSelect>
                        {settings.limitPeriodType === 'MINUTES' && (
                            <>
                                <InputInline
                                    type='number'
                                    min={1}
                                    value={settings.limitPeriodMinutes}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleLimitPeriodMinutesChange(e.target.value)}
                                />
                                <InputLabel>{T._('minutes', 'Admin')}</InputLabel>
                            </>
                        )}
                    </LimitRow>
                )}
            </div>
        );
    }
);

UsageControls.displayName = 'UsageControls';

export default UsageControls;
